import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import CryptoJS from "crypto-js";
import { ApiHost } from "../utils/ApiConfig";
import { getPermissions } from "../services/User";
import SessionWarning from "../components/common/SessionWarning";
import config from "../config";


import logger from "../utils/logger";
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const encryptData = (data, key) => {
  const jsonString = JSON.stringify(data);
  return CryptoJS.AES.encrypt(jsonString, key).toString();
};

const decryptData = (ciphertext, key) => {
  try {
    if (!ciphertext || !key) return "";

    const bytes = CryptoJS.AES.decrypt(ciphertext, key);
    const utf8 = bytes.toString(CryptoJS.enc.Utf8);

    if (!utf8 || utf8.trim() === "") {
      return "";
    }

    if (utf8.trim().startsWith("{") || utf8.trim().startsWith("[")) {
      return JSON.parse(utf8);
    }

    const cleaned = utf8.trim();
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
      return cleaned.slice(1, -1);
    }

    return cleaned;
  } catch (e) {
    return "";
  }
};

const ACTIVITY_TIMEOUT = 1800;
const HIBERNATION_THRESHOLD = 3600000;

const getDecryptedData = (key) => {
  const enc = localStorage.getItem(key);
  const refresh = localStorage.getItem("refreshToken");

  if (!enc || !refresh) {
    return "";
  }

  const result = decryptData(enc, refresh);
  return result;
};

const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const decoded = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;
    return decoded.exp > currentTime;
  } catch (e) {
    return false;
  }
};

const isTokenExpiringSoon = (token, secondsBeforeExpiry = 330) => {
  if (!token) return true;
  try {
    const decoded = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;
    return decoded.exp - currentTime < secondsBeforeExpiry;
  } catch (e) {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const maintenanceEnabled = config.maintenance?.enabled === true;
  const [isInitialized, setIsInitialized] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState("");
  const [chamberId, setChamberId] = useState("");
  const [chamberName, setChamberName] = useState("");
  const [userPermissions, setUserPermissions] = useState([]);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(300);

  const clearAuthState = useCallback(() => {
    setAccessToken("");
    setIsLoggedIn(false);
    setUserName("");
    setUserId("");
    setChamberId("");
    setChamberName("");
    setUserPermissions([]);
    setShowSessionWarning(false);

    [
      "accessToken",
      "refreshToken",
      "userName",
      "userId",
      "chamberId",
      "chamberName",
      "userPermissions",
      "lastActivity",
    ].forEach((key) => localStorage.removeItem(key));

    delete axios.defaults.headers.common["Authorization"];
  }, []);

  const restoreUserDataFromStorage = useCallback(() => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return false;

    try {
      const userData = getDecryptedData("userName");
      const userIdData = getDecryptedData("userId");
      const chamberIdData = getDecryptedData("chamberId");
      const chamberNameData = getDecryptedData("chamberName");
      const userPermissionsData = getDecryptedData("userPermissions");

      if (userData) setUserName(userData);
      if (userIdData) setUserId(userIdData);
      if (chamberIdData) setChamberId(chamberIdData);
      if (chamberNameData) setChamberName(chamberNameData);
      if (userPermissionsData) setUserPermissions(userPermissionsData);

      return true;
    } catch (e) {
      logger.error("Error restoring user data:", e);
      return false;
    }
  }, []);

  const refreshToken = useCallback(async () => {
    if (maintenanceEnabled) {
      return null;
    }

    const refresh = localStorage.getItem("refreshToken");

    if (!refresh || !isTokenValid(refresh)) {
      await clearAuthState();
      return null;
    }

    try {
      const res = await axios.post(
        ApiHost + "/refresh",
        {},
        {
          headers: {
            Authorization: `Bearer ${refresh}`,
          },
        }
      );

      if (res.status === 200 && res.data.access_token) {
        const newToken = res.data.access_token;
        setAccessToken(newToken);
        setShowSessionWarning(false);
        localStorage.setItem("accessToken", newToken);
        localStorage.setItem("lastActivity", Date.now());
        axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

        restoreUserDataFromStorage();

        return newToken;
      }
    } catch (e) {
      logger.error("Token refresh failed:", e);
      await clearAuthState();
    }
    return null;
  }, [clearAuthState, maintenanceEnabled, restoreUserDataFromStorage]);

  const checkIfIdle = useCallback(() => {
    const last = parseInt(localStorage.getItem("lastActivity") || "0");
    if (!last) return true;

    const timeDiff = Date.now() - last;
    return timeDiff > ACTIVITY_TIMEOUT * 1000;
  }, []);

  const validateSession = useCallback(async () => {
    if (maintenanceEnabled) {
      return false;
    }

    const storedAccessToken = localStorage.getItem("accessToken");
    const storedRefreshToken = localStorage.getItem("refreshToken");

    if (!storedAccessToken || !storedRefreshToken) {
      clearAuthState();
      return false;
    }

    if (!isTokenValid(storedRefreshToken)) {
      clearAuthState();
      return false;
    }

    if (!isTokenValid(storedAccessToken)) {
      const newToken = await refreshToken();
      if (!newToken) {
        clearAuthState();
        return false;
      }
      return true;
    }

    if (checkIfIdle()) {
      if (isTokenExpiringSoon(storedAccessToken, 330)) {
        setShowSessionWarning(true);
        setWarningCountdown(300);
      }
      return true;
    }

    setAccessToken(storedAccessToken);
    setIsLoggedIn(true);
    axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${storedAccessToken}`;
    restoreUserDataFromStorage();

    return true;
  }, [
    clearAuthState,
    refreshToken,
    checkIfIdle,
    restoreUserDataFromStorage,
    maintenanceEnabled,
  ]);

  useEffect(() => {
    const initAuth = async () => {
      if (maintenanceEnabled) {
        setIsInitialized(true);
        return;
      }

      await validateSession();
      setIsInitialized(true);
    };

    initAuth();
  }, [maintenanceEnabled, validateSession]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && isInitialized) {
        const lastActivity = parseInt(
          localStorage.getItem("lastActivity") || "0"
        );
        const timeDiff = Date.now() - lastActivity;

        if (timeDiff > HIBERNATION_THRESHOLD) {
          const isValid = await validateSession();
          if (!isValid) {
            clearAuthState();
          }
        }
      }
    };

    const handleFocus = async () => {
      if (isInitialized) {
        await validateSession();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isInitialized, validateSession, clearAuthState]);

  const resetActivity = useCallback(() => {
    if (!isLoggedIn) return;

    localStorage.setItem("lastActivity", Date.now());

    if (showSessionWarning && accessToken) {
      refreshToken();
    }
  }, [isLoggedIn, showSessionWarning, accessToken, refreshToken]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const events = ["click", "keydown", "mousemove", "touchstart"];

    events.forEach((event) => {
      window.addEventListener(event, resetActivity);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetActivity);
      });
    };
  }, [isLoggedIn, resetActivity]);

  useEffect(() => {
    if (!isLoggedIn || !accessToken) return;

    const interval = setInterval(async () => {
      if (checkIfIdle()) {
        if (isTokenExpiringSoon(accessToken, 330)) {
          setShowSessionWarning(true);
          setWarningCountdown(300);
        }
      } else {
        if (isTokenExpiringSoon(accessToken, 330)) {
          await refreshToken();
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isLoggedIn, accessToken, checkIfIdle, refreshToken]);

  const login_step1 = async (username) => {
    if (maintenanceEnabled) {
      return false;
    }

    try {
      const res = await axios.post(ApiHost + "/login_step1", { username });
      return res.status === 200;
    } catch (e) {
      return false;
    }
  };

  const login = async (username, password) => {
    if (maintenanceEnabled) {
      return false;
    }

    try {
      const res = await axios.post(ApiHost + "/login", { username, password });
      const {
        access_token,
        refresh_token,
        user_name,
        user_id,
        chamber_id,
        chamber_name,
      } = res.data;

      localStorage.setItem("accessToken", access_token);
      localStorage.setItem("refreshToken", refresh_token);
      localStorage.setItem("userName", encryptData(user_name, refresh_token));
      localStorage.setItem("userId", encryptData(user_id, refresh_token));
      localStorage.setItem(
        "chamberId",
        encryptData(chamber_id || "", refresh_token)
      );
      localStorage.setItem(
        "chamberName",
        encryptData(chamber_name || "", refresh_token)
      );
      localStorage.setItem("lastActivity", Date.now());

      setAccessToken(access_token);
      setIsLoggedIn(true);
      setUserName(user_name);
      setUserId(user_id);
      setChamberId(chamber_id || "");
      setChamberName(chamber_name || "");
      setShowSessionWarning(false);

      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

      try {
        const perms = await getPermissions(user_id, access_token);
        setUserPermissions(perms || []);
        localStorage.setItem(
          "userPermissions",
          encryptData(perms || [], refresh_token)
        );
      } catch (e) {
        logger.error("Error fetching permissions:", e);
        setUserPermissions([]);
      }

      return true;
    } catch (e) {
      logger.error("Login error:", e);
      return false;
    }
  };

  const logout = async () => {
    if (accessToken) {
      try {
        await axios.post(
          ApiHost + "/logout",
          {},
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      } catch (e) {}
    }

    clearAuthState();
  };

  const checkPermission = (perm) => {
    if (!isLoggedIn) return false;
    return userPermissions.some((p) => p.Name === perm);
  };

  const getConsentIds = async () => {
    if (!userId || !accessToken) return [];

    try {
      const res = await axios.post(
        ApiHost + "/users/consent/ids",
        { user_id: userId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      return res.data || [];
    } catch (e) {
      return [];
    }
  };

  const handleExtendSession = async () => {
    const newToken = await refreshToken();
    if (newToken) {
      setShowSessionWarning(false);
      resetActivity();
    } else {
      await logout();
    }
  };

  const handleSessionTimeout = async () => {
    await logout();
  };

  if (!isInitialized && !maintenanceEnabled) {
    return <div>Ładowanie...</div>;
  }

  const value = {
    accessToken,
    login_step1,
    login,
    logout,
    userId,
    userName,
    chamberId,
    chamberName,
    userPermissions,
    isLoggedIn,
    checkPermission,
    getConsentIds,
  };

  return (
    <AuthContext.Provider value={value}>
      <SessionWarning
        show={showSessionWarning && isLoggedIn && !!accessToken}
        onExtendSession={handleExtendSession}
        onTimeout={handleSessionTimeout}
        initialSeconds={warningCountdown}
      />
      {children}
    </AuthContext.Provider>
  );
};


